/**
 * CareerRoadmapPanel.js
 * AI-generated personalized career roadmap — Phase 3, Sprint 7.
 *
 * Shows a visual timeline of career milestones, skills to acquire,
 * and recommended actions based on resume + target goal.
 *
 * Props:
 *   theme        — 'dark' | 'light'
 *   resumeData   — current resume JSON
 *   authToken    — for API calls
 *   onSendToChat — callback to fire a prompt in main chat
 */
import React, { useState } from 'react';

const GOAL_OPTIONS = [
  { id: 'senior_ic',    emoji: '⚡', label: 'Senior Individual Contributor', sub: 'Senior / Staff Engineer, Senior PM' },
  { id: 'management',  emoji: '👑', label: 'Engineering / Product Manager', sub: 'Team Lead, EM, PM' },
  { id: 'faang',       emoji: '🏆', label: 'FAANG / Big Tech',              sub: 'Google, Amazon, Meta, Microsoft' },
  { id: 'startup',     emoji: '🚀', label: 'Startup / Founding Team',       sub: 'Founding Engineer, Early-stage' },
  { id: 'freelance',   emoji: '💻', label: 'Freelance / Consulting',        sub: 'Independent consultant' },
  { id: 'switch',      emoji: '🔄', label: 'Career Switch',                 sub: 'Pivot to a new domain' },
];

const MILESTONE_COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#22c55e'];

function RoadmapTimeline({ milestones, theme }) {
  const T = theme === 'dark';
  return (
    <div style={{ position: 'relative', paddingLeft: 24 }}>
      {/* Vertical line */}
      <div style={{ position: 'absolute', left: 8, top: 8, bottom: 0, width: 2,
        background: `linear-gradient(to bottom, #6366f1, transparent)`, borderRadius: 1 }} />

      {milestones.map((m, i) => {
        const color = MILESTONE_COLORS[i % MILESTONE_COLORS.length];
        return (
          <div key={i} style={{ position: 'relative', marginBottom: 20 }}>
            {/* Dot */}
            <div style={{ position: 'absolute', left: -20, top: 4, width: 10, height: 10,
              borderRadius: '50%', background: color, border: `2px solid ${T ? '#0b0b14' : '#f0f2f5'}`,
              boxShadow: `0 0 8px ${color}60` }} />

            <div style={{ background: T ? 'rgba(255,255,255,0.04)' : '#fff',
              border: `1px solid ${T ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}`,
              borderRadius: 12, padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 10,
                  background: `${color}15`, color, border: `1px solid ${color}30` }}>
                  {m.timeframe}
                </span>
                {m.is_now && (
                  <span style={{ fontSize: 8, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                    background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.25)' }}>
                    📍 YOU ARE HERE
                  </span>
                )}
              </div>
              <p style={{ fontSize: 12.5, fontWeight: 700, color: T ? 'rgba(255,255,255,0.88)' : '#0f172a', margin: '0 0 4px' }}>
                {m.title}
              </p>
              {m.description && (
                <p style={{ fontSize: 10.5, color: T ? 'rgba(255,255,255,0.5)' : '#64748b', margin: '0 0 8px', lineHeight: 1.4 }}>
                  {m.description}
                </p>
              )}
              {m.skills?.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {m.skills.map((skill, si) => (
                    <span key={si} style={{ fontSize: 9, fontWeight: 600, padding: '2px 7px', borderRadius: 8,
                      background: T ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                      color: T ? 'rgba(255,255,255,0.55)' : '#475569',
                      border: `1px solid ${T ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }}>
                      {skill}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function CareerRoadmapPanel({ theme = 'dark', resumeData, authToken, onSendToChat }) {
  const T = theme === 'dark';
  const [selectedGoal, setSelectedGoal] = useState('');
  const [targetRole, setTargetRole] = useState('');
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const authHdr = { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` };

  const generateRoadmap = async () => {
    if (!selectedGoal) { setError('Please select a career goal.'); return; }
    setLoading(true); setError(''); setRoadmap(null);

    try {
      const res = await fetch('/api/career/roadmap', {
        method: 'POST', headers: authHdr,
        body: JSON.stringify({
          goal: selectedGoal,
          target_role: targetRole,
          current_role: resumeData?.role || resumeData?.title || '',
          experience: resumeData?.experience || [],
          skills: resumeData?.skills || [],
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed');
      setRoadmap(d);
    } catch {
      // Offline fallback
      setRoadmap(generateFallbackRoadmap(selectedGoal, targetRole, resumeData));
    } finally {
      setLoading(false);
    }
  };

  const generateFallbackRoadmap = (goal, role, resume) => {
    const name = resume?.name?.split(' ')[0] || 'You';
    const currentRole = resume?.role || resume?.title || 'your current role';

    const roadmaps = {
      faang: {
        title: 'Path to FAANG',
        subtitle: `Your personalized roadmap to land a role at Google, Amazon, Meta, or Microsoft`,
        milestones: [
          { timeframe: 'Now', title: `Optimize Your Resume & LinkedIn`, is_now: true,
            description: 'ATS-optimize your resume for FAANG JDs. Get LinkedIn Premium. Connect with FAANG engineers.',
            skills: ['ATS keywords', 'LinkedIn optimization', 'Networking'] },
          { timeframe: 'Month 1-2', title: 'Core DSA Mastery',
            description: 'Complete LeetCode Top 150. Focus on arrays, trees, graphs, DP, and system design basics.',
            skills: ['LeetCode', 'Data Structures', 'Algorithms', 'Big O notation'] },
          { timeframe: 'Month 2-3', title: 'System Design Preparation',
            description: 'Study Grokking the System Design Interview. Practice designing 5+ real-world systems.',
            skills: ['Distributed systems', 'CAP theorem', 'Load balancing', 'Caching'] },
          { timeframe: 'Month 3-4', title: 'Mock Interviews & Applications',
            description: 'Do 30+ mock interviews. Apply to 10+ FAANG/tier-2 companies. Referrals strongly recommended.',
            skills: ['Behavioral prep', 'STAR format', 'Communication'] },
          { timeframe: 'Month 4-6', title: '🎯 FAANG Interview & Offer',
            description: 'Target 2-3 FAANG companies, negotiate offer, consider stock + base + bonus holistically.',
            skills: ['Offer negotiation', 'Competing offers', 'Equity understanding'] },
        ],
        action_items: [
          '📝 Start LeetCode Easy → Medium → Hard (3 problems/day)',
          '📖 Read "Designing Data-Intensive Applications" by Martin Kleppmann',
          '🤝 Get referrals — apply via FAANG employee referrals on LinkedIn',
          '🎤 Practice on Pramp.com for peer mock interviews',
        ],
        resources: ['Neetcode.io', 'LeetCode', 'System Design Primer (GitHub)', 'Grokking System Design'],
      },
      senior_ic: {
        title: 'Path to Senior Individual Contributor',
        subtitle: 'Become a Senior / Staff Engineer or Lead in 6-12 months',
        milestones: [
          { timeframe: 'Now', title: 'Assess Current Gaps', is_now: true,
            description: 'Identify gaps between your current level and Senior IC expectations at target companies.',
            skills: ['Gap analysis', 'Skill inventory', 'ATS optimization'] },
          { timeframe: 'Month 1-3', title: 'Technical Depth & Ownership',
            description: 'Lead a significant project end-to-end. Document decisions. Mentor junior engineers.',
            skills: ['System ownership', 'Technical docs', 'Mentorship', 'Code review'] },
          { timeframe: 'Month 3-6', title: 'Cross-functional Impact',
            description: 'Drive projects that affect multiple teams. Present at all-hands. Write design documents.',
            skills: ['Stakeholder management', 'Design docs', 'Cross-team collaboration'] },
          { timeframe: 'Month 6-9', title: 'Internal Promotion or Job Switch',
            description: 'Request a promotion review or apply externally with your Senior-level impact portfolio.',
            skills: ['Impact quantification', 'Resume refresh', 'Interview prep'] },
          { timeframe: 'Month 9-12', title: '🎯 Senior IC Role Secured',
            description: 'Negotiate senior-level compensation with competing offers.',
            skills: ['Negotiation', 'Equity', 'Total compensation'] },
        ],
        action_items: [
          '📊 Quantify your last 3 projects with business impact numbers',
          '📝 Write a "brag document" tracking your contributions',
          '👥 Start mentoring 1-2 junior engineers formally',
          '🔧 Lead a technical initiative from design to production',
        ],
        resources: ['Staff Engineer book by Will Larson', 'LeadDev.com', 'Engineering Ladders framework'],
      },
      switch: {
        title: 'Career Switch Roadmap',
        subtitle: 'Successfully pivot to a new domain in 4-8 months',
        milestones: [
          { timeframe: 'Now', title: 'Research & Validate Your Target', is_now: true,
            description: 'Talk to 5+ people in your target field. Confirm it aligns with your goals and strengths.',
            skills: ['Informational interviews', 'Market research', 'Skill gap analysis'] },
          { timeframe: 'Month 1-2', title: 'Bridge Skill Building',
            description: 'Take 2-3 targeted online courses. Build a portfolio project in the new domain.',
            skills: ['Online courses', 'Certifications', 'Portfolio project'] },
          { timeframe: 'Month 2-4', title: 'Build Credibility',
            description: 'Write 2-3 articles, contribute to open source, or freelance on small projects in target domain.',
            skills: ['Content creation', 'Open source', 'Freelance projects'] },
          { timeframe: 'Month 4-6', title: 'Targeted Job Applications',
            description: 'Apply to roles that value your transferable skills. Target companies undergoing transformation.',
            skills: ['Transferable skills', 'Pivot-friendly companies', 'Network activation'] },
          { timeframe: 'Month 6-8', title: '🎯 Transition Complete',
            description: 'Land your first role in the new domain. Focus on learning speed in first 90 days.',
            skills: ['Onboarding', '90-day plan', 'Learning velocity'] },
        ],
        action_items: [
          '🎯 Pick ONE target domain (not two), make it specific',
          '📚 Complete a structured course (Coursera, Udemy, or Bootcamp)',
          '💼 Build 1-2 portfolio projects to demonstrate domain skills',
          '🤝 Join 2-3 communities in your target domain on Discord/Slack',
        ],
        resources: ['Coursera', 'Udemy', 'LinkedIn Learning', 'Meetup.com for networking'],
      },
    };

    const defaultRoadmap = {
      title: `Your Career Growth Roadmap`,
      subtitle: `Personalized path based on your profile as ${name}`,
      milestones: [
        { timeframe: 'Now', title: `Strengthen ${currentRole} Profile`, is_now: true,
          description: 'Optimize your resume, LinkedIn, and personal brand for your target roles.',
          skills: ['Resume optimization', 'LinkedIn', 'Personal branding'] },
        { timeframe: 'Month 1-3', title: 'Skill Gap Closure',
          description: 'Identify and close the 2-3 most critical skill gaps for your target role.',
          skills: ['Online learning', 'Certifications', 'Side projects'] },
        { timeframe: 'Month 3-6', title: 'Network & Apply',
          description: 'Activate your network, get referrals, and apply to 20+ targeted roles.',
          skills: ['Networking', 'Referrals', 'Interview prep'] },
        { timeframe: 'Month 6-8', title: '🎯 Target Role Secured',
          description: 'Negotiate your offer and begin your next chapter.',
          skills: ['Offer negotiation', 'Counter-offer strategy'] },
      ],
      action_items: [
        '📝 Update your resume with quantified impact metrics',
        '🔗 Connect with 5 people per week in your target domain',
        '🎤 Do at least 3 mock interviews before real applications',
        '📊 Track all applications in your Application Tracker',
      ],
      resources: ['LinkedIn Learning', 'Coursera', 'Glassdoor salary data'],
    };

    return roadmaps[goal] || defaultRoadmap;
  };

  const card = {
    background: T ? 'rgba(255,255,255,0.04)' : '#fff',
    border: `1px solid ${T ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
    borderRadius: 16, padding: '16px',
    boxShadow: T ? '0 4px 20px rgba(0,0,0,0.3)' : '0 1px 8px rgba(0,0,0,0.06)',
  };

  return (
    <div style={{ padding: '20px', height: '100%', overflowY: 'auto', background: T ? '#0b0b14' : '#f0f2f5', fontFamily: "'Inter',sans-serif" }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(245,158,11,0.15)',
          border: '1px solid rgba(245,158,11,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 20 }}>🗺️</span>
        </div>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0, color: T ? 'rgba(255,255,255,0.92)' : '#0f172a' }}>
            Career Roadmap
          </h2>
          <p style={{ fontSize: 10, color: T ? 'rgba(255,255,255,0.4)' : '#64748b', margin: '2px 0 0' }}>
            {roadmap ? roadmap.subtitle : 'AI-generated personalized career growth plan'}
          </p>
        </div>
      </div>

      {!roadmap ? (
        <>
          {/* Goal selector */}
          <div style={{ ...card, marginBottom: 12 }}>
            <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
              color: T ? 'rgba(255,255,255,0.4)' : '#64748b', margin: '0 0 12px' }}>Career Goal</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {GOAL_OPTIONS.map(g => (
                <button key={g.id} onClick={() => setSelectedGoal(g.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                  border: `1.5px solid ${selectedGoal === g.id ? '#f59e0b' : T ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                  background: selectedGoal === g.id ? 'rgba(245,158,11,0.1)' : T ? 'rgba(255,255,255,0.02)' : '#f8fafc',
                }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{g.emoji}</span>
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 700, margin: '0 0 2px',
                      color: selectedGoal === g.id ? '#f59e0b' : T ? 'rgba(255,255,255,0.8)' : '#0f172a' }}>{g.label}</p>
                    <p style={{ fontSize: 10, color: T ? 'rgba(255,255,255,0.35)' : '#94a3b8', margin: 0 }}>{g.sub}</p>
                  </div>
                  {selectedGoal === g.id && (
                    <div style={{ marginLeft: 'auto', width: 18, height: 18, borderRadius: '50%', background: '#f59e0b',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
            {error && <p style={{ fontSize: 10, color: '#ef4444', margin: '8px 0 0', fontWeight: 600 }}>{error}</p>}
          </div>

          {/* Optional target role */}
          <div style={{ ...card, marginBottom: 12 }}>
            <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
              color: T ? 'rgba(255,255,255,0.4)' : '#64748b', margin: '0 0 8px' }}>Target Role (Optional)</p>
            <input
              value={targetRole}
              onChange={e => setTargetRole(e.target.value)}
              placeholder="e.g. Senior Software Engineer at Google"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 12,
                border: `1px solid ${T ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                background: T ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                color: T ? 'rgba(255,255,255,0.8)' : '#0f172a', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          <button onClick={generateRoadmap} disabled={loading || !selectedGoal} style={{
            width: '100%', padding: '13px', borderRadius: 12, border: 'none',
            background: (!selectedGoal || loading) ? (T ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)') : 'linear-gradient(135deg,#f59e0b,#ef4444)',
            color: (!selectedGoal || loading) ? (T ? 'rgba(255,255,255,0.3)' : '#94a3b8') : 'white',
            fontSize: 13, fontWeight: 700, cursor: !selectedGoal ? 'not-allowed' : 'pointer',
            boxShadow: selectedGoal ? '0 4px 18px rgba(245,158,11,0.35)' : 'none',
          }}>
            {loading ? '⏳ Building Your Roadmap...' : '🗺️ Generate My Career Roadmap'}
          </button>
        </>
      ) : (
        <>
          {/* Roadmap title */}
          <div style={{ ...card, marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 800, color: T ? 'rgba(255,255,255,0.92)' : '#0f172a', margin: '0 0 4px' }}>
              {roadmap.title}
            </h3>
            <p style={{ fontSize: 11, color: T ? 'rgba(255,255,255,0.45)' : '#64748b', margin: 0, lineHeight: 1.4 }}>
              {roadmap.subtitle}
            </p>
          </div>

          {/* Timeline */}
          <div style={{ ...card, marginBottom: 12 }}>
            <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
              color: T ? 'rgba(255,255,255,0.4)' : '#64748b', margin: '0 0 16px' }}>📍 Your Roadmap</p>
            <RoadmapTimeline milestones={roadmap.milestones || []} theme={theme} />
          </div>

          {/* Action items */}
          {roadmap.action_items?.length > 0 && (
            <div style={{ ...card, marginBottom: 12 }}>
              <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                color: T ? 'rgba(255,255,255,0.4)' : '#64748b', margin: '0 0 10px' }}>⚡ Immediate Action Items</p>
              {roadmap.action_items.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 7, alignItems: 'flex-start' }}>
                  <p style={{ fontSize: 11, color: T ? 'rgba(255,255,255,0.65)' : '#475569', margin: 0, lineHeight: 1.4 }}>{item}</p>
                </div>
              ))}
            </div>
          )}

          {/* Resources */}
          {roadmap.resources?.length > 0 && (
            <div style={{ ...card, marginBottom: 12 }}>
              <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                color: T ? 'rgba(255,255,255,0.4)' : '#64748b', margin: '0 0 10px' }}>📚 Recommended Resources</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {roadmap.resources.map((r, i) => (
                  <span key={i} style={{ fontSize: 10, fontWeight: 600, padding: '4px 10px', borderRadius: 20,
                    background: T ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.08)',
                    color: '#f59e0b', border: '1px solid rgba(245,158,11,0.2)' }}>
                    {r}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button onClick={() => onSendToChat && onSendToChat(`Based on my career goal of ${GOAL_OPTIONS.find(g=>g.id===selectedGoal)?.label || selectedGoal}, please help me create a detailed 30-60-90 day action plan with specific milestones and weekly goals.`)}
              style={{ width: '100%', padding: '11px', borderRadius: 10, border: 'none',
                background: 'linear-gradient(135deg,#f59e0b,#ef4444)', color: 'white',
                fontSize: 12, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(245,158,11,0.3)' }}>
              ⚡ Get Detailed 30-60-90 Day Plan
            </button>
            <button onClick={() => { setRoadmap(null); setSelectedGoal(''); setTargetRole(''); }}
              style={{ width: '100%', padding: '11px', borderRadius: 10,
                border: `1px solid ${T ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                background: 'transparent', color: T ? 'rgba(255,255,255,0.5)' : '#64748b',
                fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              ↩ Change Goal
            </button>
          </div>
        </>
      )}
    </div>
  );
}
